// Must stay in sync with EDITABLE_ASSIGNMENT_STATUSES in
// subject-management-details.component.ts — both gate the same edit permission.
export const SUBJECT_MANAGEMENT_EDITABLE_STATUSES = [
  'imported_pending_fix',
  'draft',
  'ready_for_teacher',
  'revision_requested',
  'special_revision_requested',
] as const;

export function canEditDetailsSection(
  offeringStatus = 'draft',
  assignmentStatuses: (string | null | undefined)[] = [],
): boolean {
  const editableStatuses = new Set<string>(SUBJECT_MANAGEMENT_EDITABLE_STATUSES);
  const normalizedAssignments = assignmentStatuses
    .map((status) => String(status ?? '').trim())
    .filter((status) => status !== '');

  return normalizedAssignments.length > 0
    ? normalizedAssignments.some((status) => editableStatuses.has(status))
    : editableStatuses.has(String(offeringStatus).trim());
}

export function buildSelectedTeachingDateLabel(
  sequenceDay: string | null | undefined,
  fallbackLabel: string,
  formattedDate: string,
): string {
  const normalizedSequenceDay = sequenceDay?.trim();
  const label = normalizedSequenceDay != null && normalizedSequenceDay !== ''
    ? normalizedSequenceDay
    : fallbackLabel;

  return `${label} - ${formattedDate}`;
}

export function buildTeachingDateSequenceLabel(
  sequenceDay: string | null | undefined,
  fallbackLabel: string,
  timeLabel: string,
): string {
  const normalizedSequenceDay = sequenceDay?.trim();
  const baseLabel = normalizedSequenceDay != null && normalizedSequenceDay !== ''
    ? normalizedSequenceDay
    : fallbackLabel;
  const normalizedTimeLabel = timeLabel.trim();
  const baseLabelAlreadyHasTime = /\(\s*\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\s*\)/.test(baseLabel);

  if (normalizedTimeLabel === '' || baseLabelAlreadyHasTime) {
    return baseLabel;
  }

  return `${baseLabel} (${normalizedTimeLabel})`;
}

function parseErrorResponse(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function getFirstErrorText(...values: unknown[]): string {
  return values
    .find((value): value is string => typeof value === 'string' && value.trim() !== '')
    ?.trim() ?? '';
}

export function getSubjectManagementDetailsApiErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  const responseError = parseErrorResponse(
    (error as { error?: unknown } | null | undefined)?.error ?? error
  );

  if (responseError != null && typeof responseError === 'object') {
    const errorRecord = responseError as { errors?: unknown; error?: unknown; message?: unknown };
    const errors = Array.isArray(errorRecord.errors)
      ? errorRecord.errors
        .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
        .map((item) => item.trim())
      : [];

    if (errors.length > 0) {
      return errors.join('\n');
    }

    return getFirstErrorText(errorRecord.error, errorRecord.message, fallbackMessage);
  }

  return getFirstErrorText(responseError, (error as { message?: unknown } | null | undefined)?.message, fallbackMessage);
}
